

import { mapActions, mapState, mapWritableState } from "pinia";
import { ElementResizingMixinStaging } from "%/dynamic-component-editor/mixins/element-resizing-mixin-staging";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useConfigurationPanelStore = (...args) => require("%/dynamic-component-editor/stores/configuration-panel-store").useConfigurationPanelStore(...args);
// eslint-disable-next-line global-require
const useDynamicComponentEditorStore = (...args) => require("%/dynamic-component-editor/stores/dynamic-component-editor").default(...args);


const CanvasEventHandlerMixinStaging = {
  data() {
    return {
      mouseOver: false,
      mouseOverActions: false,
      lastClickedAtMilliseconds: 0,
    };
  },
  mixins: [ElementResizingMixinStaging],
  computed: {
    ...mapWritableState(useConfigurationPanelStore, [
      "newDynamicComponentLayout",
    ]),
    ...mapWritableState(useDynamicComponentEditorStore, [
      "detachingElementIdentifier",
    ]),
    ...mapState(useDynamicComponentEditorStore, [
      "showContextualMenuForIdInBuilder",
    ]),
  },
  methods: {
    ...mapActions(useDynamicComponentEditorStore, [
      "handleOnEdit",
      "handleOnEditParent",
      "handleDuplicate",
      "handleOnDelete",
      "setElementToFocusOnHover",
      "setShowContextualMenuForIdInBuilder",
      "wrapElementInContainer",
      "wrapSelectionInContainer",
      "getLayoutOfElementByIdentifier",
      "isEditable",
      "handleDetach",
      "canWrapCurrentSelection",
    ]),
    internalMouseOverHandler() {
      this.mouseOver = true;
      this.setElementToFocusOnHover(this.identifier);
    },
    internalMouseLeaveHandler() {
      if (this.resizing) {
        return false;
      }
      this.mouseOver = false;
      this.setElementToFocusOnHover(null);
    },
    internalHandleOnEdit(event) {
      if (sessionStorage.getItem("resizing")) {
        return false;
      }
      // simulate a mousedown event to trigger the click outside directive
      if (this.editMode) {
        const mousedownEvent = new MouseEvent("mousedown", {
          bubbles: true,
          cancelable: true,
          view: window,
        });
        const mouseupEvent = new MouseEvent("mouseup", {
          bubbles: true,
          cancelable: true,
          view: window,
        });

        document.querySelector("body").dispatchEvent(mousedownEvent);
        document.querySelector("body").dispatchEvent(mouseupEvent);
      }

      this.setShowContextualMenuForIdInBuilder(null);
      const millisecondsSinceLastClick = new Date().getTime() - this.lastClickedAtMilliseconds;
      if (millisecondsSinceLastClick < 300) {
        this.handleOnEditParent(this.identifier);
      } else {
        this.lastClickedAtMilliseconds = new Date().getTime();
        if (this.isEditable) {
          this.handleOnEdit(this.identifier, event);
        }
      }
      this.lastClickedAtMilliseconds = new Date().getTime();
      useDynamicComponentEditorStore().setActiveBreakpointByAlias(this.breakpoint);
    },
    internalHandleDuplicate() {
      if (this.breakpoint) {
        return;
      }
      this.handleDuplicate(this.identifier);
    },
    internalHandleOnDelete() {
      if (this.breakpoint) {
        return;
      }
      this.handleOnDelete(this.identifier);
    },
    internalCreateCustomComponent() {
      this.newDynamicComponentLayout = this.getLayoutOfElementByIdentifier(this.identifier);
      const name = useDynamicComponentEditorStore().generateComponentName();
      useConfigurationPanelStore().createDynamicComponentWithoutTemplate(name, {}, id => {
        window.location.href = `${useConfigurationPanelStore().webLink}/dynamic-component/${id}`;
      });
    },
    internalWrapInHorizontalContainer() {
      if (this.breakpoint) {
        return;
      }
      this.wrapElementInContainer("HorizontalContainerWrapper", this.identifier);
    },
    internalWrapInVerticalContainer() {
      if (this.breakpoint) {
        return;
      }
      this.wrapElementInContainer("VerticalContainerWrapper", this.identifier);
    },
    internalWrapSelectionInVerticalContainer() {
      if (this.breakpoint) {
        return;
      }
      this.wrapSelectionInContainer("VerticalContainerWrapper", this.currentlyInspectedElementIdentifiers);
    },
    internalWrapSelectionInHorizontalContainer() {
      if (this.breakpoint) {
        return;
      }
      this.wrapSelectionInContainer("HorizontalContainerWrapper", this.currentlyInspectedElementIdentifiers);
    },
    internalCreateDynamicComponentFromIdentifier() {
      useConfigurationPanelStore().createDynamicComponentFromIdentifier(this.identifier);
    },
    internalHandleCopy() {
      useDynamicComponentEditorStore().handleClipboardPublicationElementTransfer(this.dynamicComponentInstance.getElement(this.identifier), "copy");
    },
    internalHandlePaste() {
      useDynamicComponentEditorStore().handleClipboardPublicationElementPaste();
    },
    internalHandleDetach() {
      this.detachingElementIdentifier = this.identifier;
    },
  },
};

export { CanvasEventHandlerMixinStaging };
